// src/components/instructor/OrgOverview.js
//
// Pass 51 — the REAL SAS operational Overview. Every metric is derived from canonical Firestore data
// (bookings, members, cases) already loaded by the shell — no fabricated values. Replaces the giant
// legacy OrganizationDashboardPage that used to back the Overview tab. Each card links into the
// Bookings / Team / Clients workspaces.

import React, { useMemo } from 'react';
import MotusIcon from '../shared/MotusIcon';
import SyncNowPanel from './ops/SyncNowPanel';
import { deriveClients, activeSeasonClients } from '../../utils/clientsWorkspace';
import { opsDateKey, opsShiftDays, opsNow } from '../../utils/opsTime';
import { scopeToSeason } from '../../utils/seasonScope';
import { buildUnassignedQueue, getOperationalRoster } from '../../utils/operationsWorkspace';
import { CURRENT_SEASON_ID, isCurrentSasTeachingMember, isCurrentSasOperationsMember } from '../../utils/instructorCategory';
import { isClientBooking, isLiveBooking } from '../../utils/scheduleModel';

const C = {
  card:'#FFFFFF', card2:'#F5F4F0', border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', green:'#22C55E', orange:'#F05A1F', purple:'#9333EA', gold:'#D97706', red:'#DC2626',
  t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";
const fontD = "'Bebas Neue',sans-serif";

const isUnassigned = (b) => !b.instructorId;
const msOf = (v) => (v == null ? null : v?.toDate ? v.toDate().getTime() : v?.seconds ? v.seconds * 1000 : (v instanceof Date ? v.getTime() : (Number.isNaN(new Date(v).getTime()) ? null : new Date(v).getTime())));

// Pass 58F2 — PRIMARY operational stats. ALL metrics are scoped to the CURRENT live season and to LIVE
// bookings (retired/superseded seed placeholders and cancellations excluded), bucketed by the canonical
// Asia/Tokyo operating day. Unassigned reuses the SAME canonical helper (buildUnassignedQueue) as the
// Unassigned workspace, so the Overview never becomes a competing calculation. `now` defaults to real
// time; tests pass a fixed instant.
export function overviewPrimaryStats(bookings = [], now = opsNow()) {
  const season = scopeToSeason(bookings || [], CURRENT_SEASON_ID);
  const live = season.filter(isLiveBooking);            // exclude cancelled + retired/superseded
  const liveClient = live.filter(isClientBooking);      // client lessons only
  const nowMs = now.getTime();
  const todayKey = opsDateKey(now);
  const tomorrowKey = opsDateKey(opsShiftDays(now, 1));
  const in7Ms = opsShiftDays(now, 7).getTime();
  const onDay = (k) => liveClient.filter((b) => opsDateKey(b.startAt) === k);
  const today = onDay(todayKey);
  const tomorrow = onDay(tomorrowKey);
  // SAME canonical definition as the Unassigned workspace badge (season-scoped, live, no instructor).
  const unassignedQueue = buildUnassignedQueue(season);
  const needsReview = liveClient.filter((b) => b.needsReview);
  const needsAttention = liveClient.filter((b) => {
    const t = msOf(b.startAt);
    return b.needsReview || (isUnassigned(b) && t != null && t >= nowMs && t < in7Ms);
  });
  return {
    today: today.length,
    todayUnassigned: today.filter(isUnassigned).length,
    tomorrow: tomorrow.length,
    tomorrowUnassigned: tomorrow.filter(isUnassigned).length,
    unassigned: unassignedQueue.length,
    needsAttention: needsAttention.length,
    needsReview: needsReview.length,
  };
}

// Pass 58G1 — SECONDARY (team + clients). Teaching Team is the STRICT current SAS School teaching count
// (positive current evidence: confirmed-current + SAS School + not-explicitly-non-teaching), NOT the
// permissive splitTeachingStaff absence-of-false rule. It excludes John (owner, non-teaching), operations
// staff, disconnected Collective profiles, and review members. Operations = confirmed-current non-teaching
// SAS staff (owner + drivers). Clients: the all-time canonical roster; "active this season" is an ACTIVITY
// property of that same canonical index (activeSeasonClients) so it matches Clients → Active exactly.
export function overviewSecondaryStats(bookings = [], members = [], cases = []) {
  const roster = getOperationalRoster(members || [], bookings || [], CURRENT_SEASON_ID); // confirmed-current
  const teaching = roster.filter((m) => isCurrentSasTeachingMember(m, CURRENT_SEASON_ID));
  const operations = roster.filter((m) => isCurrentSasOperationsMember(m, CURRENT_SEASON_ID));
  const clients = deriveClients(bookings || [], members || [], cases || []);
  const seasonLiveClient = scopeToSeason(bookings || [], CURRENT_SEASON_ID).filter(isLiveBooking).filter(isClientBooking);
  const activeThisSeason = activeSeasonClients(clients, seasonLiveClient);
  return {
    teaching: teaching.length,
    roster: roster.length,
    operations: operations.length,
    clients: clients.length,
    activeThisSeason: activeThisSeason.length,
  };
}

export default function OrgOverview({ bookings, allBookings = null, members, cases = [], onNavigate }) {
  // §9 fast core: PRIMARY (today/tomorrow/unassigned/attention) renders as soon as the fast CURRENT-season
  // feed (`bookings`) arrives and never blocks on anything else. Pass 87.2 — SECONDARY (teaching team +
  // the ALL-TIME client directory count) derives from `allBookings`, the lazily-loaded all-seasons
  // snapshot; it stays a quiet "…" until that history arrives, so the primary row is never gated on it.
  const primary = useMemo(() => (bookings == null ? null : overviewPrimaryStats(bookings)), [bookings]);
  const secondary = useMemo(() => {
    if (members == null || allBookings == null) return null;
    return overviewSecondaryStats(allBookings || [], members || [], cases || []);
  }, [allBookings, members, cases]);

  if (primary == null) return <div style={{ padding:'40px 0', color:C.t3, fontSize:14 }}>Loading today's operations…</div>;

  // Pass 57C: org id for the ops Sync Now panel (owner/admin/ops only, gated inside the panel).
  const orgId = (members || []).find((m) => m && m.organizationId)?.organizationId || '';

  return (
    <div>
      {orgId ? <div style={{ marginBottom: 16 }}><SyncNowPanel organizationId={orgId} /></div> : null}
      {/* Primary operational row — today / tomorrow / unassigned / needs attention (renders first) */}
      <div className="m2" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:16 }}>
        <Metric label="Today's Lessons" value={primary.today} color={C.blue}
          sub={primary.today === 0 ? 'No lessons today' : (primary.todayUnassigned > 0 ? `${primary.todayUnassigned} unassigned` : 'All assigned')}
          subColor={primary.todayUnassigned > 0 ? C.orange : C.t3} onClick={() => onNavigate('bookings', 'day')} />
        <Metric label="Tomorrow's Lessons" value={primary.tomorrow} color={C.green}
          sub={primary.tomorrow === 0 ? 'No lessons tomorrow' : (primary.tomorrowUnassigned > 0 ? `${primary.tomorrowUnassigned} unassigned` : 'All assigned')}
          subColor={primary.tomorrowUnassigned > 0 ? C.orange : C.t3} onClick={() => onNavigate('bookings', 'tomorrow')} />
        <Metric label="Unassigned (upcoming)" value={primary.unassigned} color={primary.unassigned > 0 ? C.orange : C.t2}
          sub={primary.unassigned > 0 ? 'Awaiting an instructor' : 'All assigned'} subColor={primary.unassigned > 0 ? C.orange : C.green}
          onClick={() => onNavigate('bookings', 'unassigned')} />
        <Metric label="Needs Attention" value={primary.needsAttention} color={primary.needsAttention > 0 ? C.red : C.green}
          sub={primary.needsAttention > 0 ? (primary.needsReview > 0 ? `${primary.needsReview} flagged for review` : 'Unassigned within 7 days') : 'Nothing needs review'}
          subColor={primary.needsAttention > 0 ? C.orange : C.green} onClick={() => onNavigate('bookings', 'unassigned')} />
      </div>

      {/* Secondary row — teaching team / clients (progressive; never blocks the row above) */}
      <div className="m2" style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:20 }}>
        <Metric label="Teaching Team" value={secondary ? secondary.teaching : '…'} color={C.purple}
          sub={secondary ? (secondary.operations > 0 ? `${secondary.teaching} teaching · ${secondary.operations} operations` : 'Current instructors') : 'Loading…'}
          onClick={() => onNavigate('team')} />
        <Metric label="Clients" value={secondary ? secondary.clients : '…'} color={C.blue}
          sub={secondary ? `${secondary.activeThisSeason} active this season` : 'Loading…'} subColor={C.t3}
          onClick={() => onNavigate('clients')} />
      </div>

      {/* Quick jumps */}
      <div className="m1" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
        <Jump title="Open Bookings" desc="Requests, calendar, day, week, season and unassigned lessons." icon="clipboard" color={C.blue} onClick={() => onNavigate('bookings')} />
        <Jump title="Manage Team" desc="Current team, all people, invites & access, roster review." icon="user" color={C.purple} onClick={() => onNavigate('team')} />
        <Jump title="Clients" desc="Client directory, current-season activity and booking history." icon="target" color={C.green} onClick={() => onNavigate('clients')} />
      </div>
    </div>
  );
}

function Metric({ label, value, color, sub, subColor, onClick }) {
  return (
    <button onClick={onClick} style={{ textAlign:'left', background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'18px 20px', cursor:'pointer', transition:'all .18s', fontFamily:font }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderH; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'none'; }}>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:C.t3, marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:fontD, fontSize:44, lineHeight:1, color }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:subColor || C.t3, marginTop:5 }}>{sub}</div>}
    </button>
  );
}

function Jump({ title, desc, icon, color, onClick }) {
  return (
    <button onClick={onClick} style={{ textAlign:'left', background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'20px 22px', cursor:'pointer', transition:'all .18s', fontFamily:font }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderH; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'none'; }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
        <div style={{ width:38, height:38, borderRadius:10, background:`${color}14`, border:`1px solid ${color}22`, display:'flex', alignItems:'center', justifyContent:'center' }}><MotusIcon name={icon} size={18} color={color} /></div>
        <div style={{ fontFamily:fontD, fontSize:20, letterSpacing:1, color:C.t1 }}>{title}</div>
      </div>
      <div style={{ fontSize:13, color:C.t2, lineHeight:1.55 }}>{desc}</div>
    </button>
  );
}
